'use client';

import { useState, useEffect } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { getDashboardStats, updateSiteName } from '@/lib/api';
import Link from 'next/link';

interface SiteBreakdown {
  site_number: string;
  name: string;
  documents: number;
  vectors: number;
  cost_usd: number;
}

interface Stats {
  sites: number;
  documents: number;
  tables: number;
  cells: number;
  audit_logs: number;
  feedback: number;
  vectors: number;
  total_cost_usd: number;
  cost_by_event: { event_type: string; cost_usd: number }[];
  site_breakdown: SiteBreakdown[];
}

export default function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [editingSite, setEditingSite] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getDashboardStats().then(setStats).catch(() => {});
  }, []);

  const startEdit = (s: SiteBreakdown) => {
    setEditingSite(s.site_number);
    setEditName(s.name);
  };

  const saveName = async (siteNumber: string) => {
    setSaving(true);
    try {
      await updateSiteName(siteNumber, editName);
      setStats(prev => prev ? {
        ...prev,
        site_breakdown: prev.site_breakdown.map(s =>
          s.site_number === siteNumber ? { ...s, name: editName } : s
        ),
      } : prev);
      setEditingSite(null);
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const fmt = (n: number) => n < 0.001 ? `$${n.toFixed(6)}` : `$${n.toFixed(4)}`;

  const cards = stats ? [
    { label: 'Sites', value: stats.sites, icon: 'fa-map-marker-alt', color: 'info', href: '/sites' },
    { label: 'Documents', value: stats.documents, icon: 'fa-file-alt', color: 'success', href: '/sites' },
    { label: 'Tables', value: stats.tables, icon: 'fa-table', color: 'warning', href: '/tables' },
    { label: 'Cells', value: stats.cells.toLocaleString(), icon: 'fa-th', color: 'primary', href: '/cells' },
    { label: 'Vectors', value: stats.vectors.toLocaleString(), icon: 'fa-database', color: 'secondary', href: '/vectors' },
    { label: 'Queries Logged', value: stats.audit_logs, icon: 'fa-clipboard-list', color: 'dark', href: '/logs' },
    { label: 'Total API Cost', value: fmt(stats.total_cost_usd), icon: 'fa-dollar-sign', color: 'danger', href: '/costs' },
  ] : [];

  return (
    <AdminLayout title="Dashboard" subtitle="System overview" breadcrumbs={[{ label: 'Dashboard' }]}>
      {!stats ? (
        <div className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-muted" /></div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="row">
            {cards.map(c => (
              <div key={c.label} className="col-lg-2 col-md-4 col-sm-6 col-12">
                <div className={`small-box bg-${c.color}`}>
                  <div className="inner">
                    <h3>{c.value}</h3>
                    <p>{c.label}</p>
                  </div>
                  <div className="icon"><i className={`fas ${c.icon}`} /></div>
                  {c.href ? (
                    <Link href={c.href} className="small-box-footer">
                      View <i className="fas fa-arrow-circle-right" />
                    </Link>
                  ) : (
                    <div className="small-box-footer">&nbsp;</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Site breakdown */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title"><i className="fas fa-sitemap mr-2" />Site Breakdown</h3>
            </div>
            <div className="card-body table-responsive p-0">
              <table className="table table-hover table-sm">
                <thead>
                  <tr>
                    <th>Site Number</th>
                    <th>Name</th>
                    <th>Documents</th>
                    <th>Vectors</th>
                    <th>Cost (USD)</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.site_breakdown.map(s => (
                    <tr key={s.site_number}>
                      <td><span className="badge badge-info">{s.site_number}</span></td>
                      <td>
                        {editingSite === s.site_number ? (
                          <div className="d-flex align-items-center" style={{ gap: 4 }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              style={{ maxWidth: 200 }}
                              value={editName}
                              onChange={e => setEditName(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') saveName(s.site_number);
                                if (e.key === 'Escape') setEditingSite(null);
                              }}
                              autoFocus
                              disabled={saving}
                            />
                            <button className="btn btn-xs btn-success" onClick={() => saveName(s.site_number)} disabled={saving}>
                              <i className={saving ? 'fas fa-spinner fa-spin' : 'fas fa-check'} />
                            </button>
                            <button className="btn btn-xs btn-secondary" onClick={() => setEditingSite(null)} disabled={saving}>
                              <i className="fas fa-times" />
                            </button>
                          </div>
                        ) : (
                          <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => startEdit(s)}
                            title="Click to edit name"
                          >
                            {s.name || <span className="text-muted font-italic">Click to set name</span>}
                            <i className="fas fa-pencil-alt ml-2 text-muted" style={{ fontSize: '0.7rem' }} />
                          </span>
                        )}
                      </td>
                      <td>{s.documents}</td>
                      <td>{s.vectors.toLocaleString()}</td>
                      <td className="text-danger font-weight-bold">{fmt(s.cost_usd)}</td>
                      <td>
                        <Link href={`/sites/${s.site_number}`} className="btn btn-xs btn-outline-primary mr-1">
                          <i className="fas fa-folder-open mr-1" />Docs
                        </Link>
                        <Link href={`/sites/${s.site_number}/query`} className="btn btn-xs btn-outline-success">
                          <i className="fas fa-comments mr-1" />Query
                        </Link>
                      </td>
                    </tr>
                  ))}
                  {stats.site_breakdown.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-muted py-4">No sites yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
